/**
 * Build Plan Dialog Component
 *
 * Shows real-time progress of protocol modification plan generation
 * via WebSocket and displays the plan when complete.
 */

import React, {useEffect, useState, useCallback, useRef} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Alert,
  Chip,
  Paper,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import {
  createWebSocket,
  getJobStatus,
  ProgressEvent,
} from '../services/agent';
import {PlanActionsTable} from './PlanActionsTable';

interface BuildPlanDialogProps {
  open: boolean;
  onClose: () => void;
  jobId: string | null;
  trialId: string;
  version1: string;
  version2: string;
}

export const BuildPlanDialog: React.FC<BuildPlanDialogProps> = ({
  open,
  onClose,
  jobId,
  trialId,
  version1,
  version2,
}) => {
  const [status, setStatus] = useState<string>('pending');
  const [progressMessages, setProgressMessages] = useState<ProgressEvent[]>([]);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [ws, setWs] = useState<WebSocket | null>(null);
  const progressEndRef = useRef<HTMLDivElement>(null);

  // Handle WebSocket message
  const handleMessage = useCallback((event: ProgressEvent) => {
    setProgressMessages(prev => [...prev, event]);
    setStatus(event.status);

    // If we receive result data, store it
    if (event.data && event.status === 'completed') {
      setResult(event.data);
    }
  }, []);

  // Handle WebSocket error
  const handleError = useCallback(() => {
    setError('Connection lost. Fetching latest status...');

    // Poll for status if WebSocket fails
    if (jobId) {
      getJobStatus(jobId).then(job => {
        setStatus(job.status);
        if (job.result) {
          setResult(job.result);
        }
        if (job.error) {
          setError(job.error);
        }
      });
    }
  }, [jobId]);

  // Auto-scroll to latest message
  useEffect(() => {
    if (progressEndRef.current) {
      progressEndRef.current.scrollIntoView({behavior: 'smooth'});
    }
  }, [progressMessages]);

  // Connect WebSocket when dialog opens with a jobId
  useEffect(() => {
    if (open && jobId) {
      const websocket = createWebSocket(
        jobId,
        handleMessage,
        handleError,
        () => console.log('WebSocket closed')
      );

      setWs(websocket);

      return () => {
        websocket.close();
      };
    }
  }, [open, jobId, handleMessage, handleError]);

  // Cleanup on dialog close
  const handleClose = () => {
    if (ws) {
      ws.close();
    }
    onClose();
  };

  // Handle backdrop/ESC close - only allow if not running
  const handleBackdropClose = (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
    // Prevent closing during plan generation
    if (status === 'running' || status === 'pending') {
      return;
    }
    handleClose();
  };

  // Render status icon
  const renderStatusIcon = () => {
    switch (status) {
      case 'pending':
        return <PendingIcon color="info" />;
      case 'running':
        return <BuildIcon color="primary" className="rotating" />;
      case 'completed':
        return <CheckIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      default:
        return <PendingIcon />;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleBackdropClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          {renderStatusIcon()}
          <Typography variant="h6" component="span">
            Protocol Modification Plan: {trialId} ({version1} → {version2})
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Status Progress */}
        {status === 'running' && (
          <Box mb={2}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" mt={1}>
              Generating plan... This may take a few minutes.
            </Typography>
          </Box>
        )}

        {/* Error Alert */}
        {error && status !== 'completed' && (
          <Alert severity="error" sx={{mb: 2}}>
            {error}
          </Alert>
        )}

        {/* Progress Messages */}
        {progressMessages.length > 0 && status !== 'completed' && (
          <Paper elevation={1} sx={{mb: 2, maxHeight: 300, overflow: 'auto'}}>
            <List dense>
              {progressMessages.map((msg, idx) => (
                <ListItem key={idx}>
                  <ListItemText
                    primary={msg.message}
                    secondary={
                      msg.timestamp
                        ? new Date(msg.timestamp).toLocaleTimeString()
                        : ''
                    }
                  />
                </ListItem>
              ))}
              <div ref={progressEndRef} />
            </List>
          </Paper>
        )}

        {/* Completed Result */}
        {status === 'completed' && result && (
          <Box>
            <Alert severity="success" sx={{mb: 3}}>
              <Typography variant="body2">
                ✅ Plan generated successfully!
              </Typography>
              <Box mt={1}>
                <Chip
                  label={`${trialId}`}
                  size="small"
                  sx={{mr: 1}}
                />
                <Chip
                  label={`${version1} → ${version2}`}
                  size="small"
                  color="primary"
                />
              </Box>
            </Alert>

            {/* Render the plan as a beautiful table */}
            <PlanActionsTable
              plan={result.plan ? JSON.parse(result.plan) : {actions: []}}
              cached={result.cached}
              generatedAt={result.generated_at}
            />
          </Box>
        )}

        {/* Failed State */}
        {status === 'failed' && (
          <Alert severity="error">
            <Typography variant="body2" fontWeight="bold">
              Plan generation failed
            </Typography>
            <Typography variant="body2">
              {error || 'An unknown error occurred'}
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          variant={status === 'completed' ? 'contained' : 'outlined'}
          color={status === 'running' ? 'warning' : 'primary'}
        >
          {status === 'completed' ? 'Close' : status === 'running' ? 'Cancel' : 'Cancel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// CSS for rotating icon
const style = document.createElement('style');
style.textContent = `
  @keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .rotating {
    animation: rotate 2s linear infinite;
  }
`;
document.head.appendChild(style);
